const MAX_IMAGE_BYTES = 4 * 1024 * 1024;
const MAX_DIMENSION = 1536;

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function renderImageToDataUrl(img: HTMLImageElement): string | null {
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return null;

  const scale = Math.min(1, MAX_DIMENSION / Math.max(w, h));
  const cw = Math.max(1, Math.round(w * scale));
  const ch = Math.max(1, Math.round(h * scale));

  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(img, 0, 0, cw, ch);
  return canvas.toDataURL('image/jpeg', 0.88);
}

async function waitForImageLoad(img: HTMLImageElement): Promise<boolean> {
  if (img.complete && img.naturalWidth > 0) return true;
  return new Promise((resolve) => {
    const onLoad = () => {
      cleanup();
      resolve(img.naturalWidth > 0);
    };
    const onError = () => {
      cleanup();
      resolve(false);
    };
    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
    };
    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
  });
}

async function fetchImageAsDataUrl(absoluteUrl: string): Promise<string | null> {
  try {
    const response = await fetch(absoluteUrl, {
      credentials: 'include',
      cache: 'force-cache',
    });
    if (!response.ok) return null;
    const blob = await response.blob();
    if (!blob.type.startsWith('image/') || blob.size === 0) return null;
    if (blob.size > MAX_IMAGE_BYTES) return null;
    const dataUrl = await blobToDataUrl(blob);
    return dataUrl.startsWith('data:image/') ? dataUrl : null;
  } catch {
    return null;
  }
}

/**
 * Encode l’image affichée en data URI base64 pour l’analyse vision Mistral.
 * Préfère le canvas (pixels réellement affichés), puis fetch same-origin.
 */
export async function imageElementToBase64DataUrl(
  img: HTMLImageElement,
  resolveAbsoluteUrl: (el: HTMLImageElement) => string,
): Promise<string | null> {
  const loaded = await waitForImageLoad(img);
  if (loaded) {
    try {
      const fromCanvas = renderImageToDataUrl(img);
      if (fromCanvas?.startsWith('data:image/')) return fromCanvas;
    } catch {
      /* canvas tainted (CORS) */
    }
  }

  const absoluteUrl = resolveAbsoluteUrl(img);
  if (!absoluteUrl || absoluteUrl.startsWith('data:image/')) {
    return absoluteUrl.startsWith('data:image/') ? absoluteUrl : null;
  }

  return fetchImageAsDataUrl(absoluteUrl);
}
